import React, { useMemo, useState } from 'react';
import { Row, Col, Empty, Pagination, message } from 'antd';
import startCase from 'lodash/startCase';
import DashboardRow from '../../../shared/DashboardRow';
import Item from './Item';

import { DASHBOARD_TYPES, GQL_InvestigationTeacherEntry } from '../../../types/investigation';
import { formatDistance } from '../../../utils/date';
import InvestigationIcon from '../../../shared/InvestigationIcon';
import DashboardLoadingCard from '../../../shared/DashboardLoadingCard';
import { useAuth } from '../../../hooks/useAuth';
import { useApolloClient, useQuery } from '@apollo/client';
import { gqlSchema } from '../../../gql/schema';

interface IActiveInvestigationsRow {
  investigations?: GQL_InvestigationTeacherEntry[];
  loading?: boolean;
}

const ActiveInvestigationsRow = (props: IActiveInvestigationsRow) => {
  const { isFacilitator } = useAuth();
  const [pageActiveInv, setPageActiveInv] = useState(1);
  const pageSize = 3;
  const client = useApolloClient();

  // GraphQL query for paginated investigations
  const { data, loading } = useQuery<{
    getInvestigationsByTeacherLimited: GQL_InvestigationTeacherEntry[];
  }>(gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationsByTeacherLimited, {
    variables: { filter: { page: pageActiveInv, pageSize, type: DASHBOARD_TYPES.ACTIVE_INVESTIGATIONS } },
    onError: (error) => {
      message.error(error.message || 'Error loading investigations.');
    },
    fetchPolicy: 'cache-and-network',
  });

  const investigations = data?.getInvestigationsByTeacherLimited || [];

  const activeInvestigations = useMemo(() => {
    if (loading) {
      return (
        <Row gutter={[24, 24]}>
          {[...Array(3)].map((_, index) => (
            <Col key={index} xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}>
              <DashboardLoadingCard />
            </Col>
          ))}
        </Row>
      );
    }

    if (!investigations.length) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Active Investigations" />;
    }

    return (
      <Row gutter={[24, 24]}>
        {investigations.map((item: GQL_InvestigationTeacherEntry, index) => (
          <Col key={`${item.id}-${index}`} xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}>
            <Item
              className={item.class}
              progress={item.completion * 100}
              classColor={item.classColorHex}
              timeLeft={`${formatDistance(item.dueDate)} left`}
              investigationName={`${startCase(item.discipline?.name?.toLowerCase())}: ${item.title}`}
              investigationId={item.id}
              timeWarning={false}
              type={item.discipline?.subject === 'Engineering' ? 'Design Challenge' : 'Investigation'}
              classId={item.classId}
              icon={<InvestigationIcon subject={item.discipline?.subject || ''} discipline={item.discipline?.name || ''} />}
            />
          </Col>
        ))}
      </Row>
    );
  }, [investigations, loading]);
  const totalInvestigationsActiveInv = investigations?.length || 0;
  const hasMorePagesActiveInv = totalInvestigationsActiveInv >= 3;
  const handlePageChangeFinishedInv = async (newPage: number, type: DASHBOARD_TYPES) => {
    try {
      const { data } = await client.query({
        query: gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationsByTeacherLimited,
        variables: {
          filter: { page: newPage, pageSize, type },
        },
        fetchPolicy: 'network-only',
      });

      const nextPageData = data?.getInvestigationsByTeacherLimited || [];

      if (nextPageData.length === 0) {
        message.info(`No more ${type} available, returning to the previous page.`);
        return;
      }

      setPageActiveInv(newPage);
    } catch (error) {
      message.error('Failed to load data. Please try again.');
    }
  };

  return (
    <>
      <DashboardRow
        title={`Active ${isFacilitator ? 'Workshop' : ''} Investigations`}
        items={activeInvestigations}
      />
      <Pagination
        current={pageActiveInv}
        pageSize={pageSize}
        total={hasMorePagesActiveInv ? (pageActiveInv + 1) * pageSize : pageActiveInv * pageSize}
        onChange={(newPage) => handlePageChangeFinishedInv(newPage, DASHBOARD_TYPES.ACTIVE_INVESTIGATIONS)}
        showSizeChanger={false}
        hideOnSinglePage={totalInvestigationsActiveInv <= pageSize}
        style={{ textAlign: 'center', marginTop: 16 }}
        disabled={loading}
      />
    </>
  );
};



export default ActiveInvestigationsRow;
